import type { PropsWithChildren } from "react";
import { IntlProvider } from "react-intl";

type IntlError = {
  code: string;
  message?: string;
};

const DEFAULT_LOCALE = "en";

/**
 * Resolves the active locale for the browser session.
 *
 * Falls back to the application default during SSR-like execution or when the
 * browser does not expose a language value.
 *
 * @returns {string} Active locale string for the intl provider.
 */
const resolveLocale = (): string => {
  if (typeof navigator === "undefined") {
    return DEFAULT_LOCALE;
  }

  return navigator.language || DEFAULT_LOCALE;
};

/**
 * Silences expected MISSING_TRANSLATION errors.
 *
 * The application uses `defaultMessage` as the primary text source with an
 * empty messages map, so every `formatMessage` call would otherwise log a
 * noisy console error.  Real formatting errors are still forwarded to the
 * default handler.
 * @param  {IntlError} error - The error object from react-intl.
 * @returns {void} Does not return a value. No return value; errors are logged or silenced based on type.
 */
const handleIntlError = (error: IntlError): void => {
  if (error.code === "MISSING_TRANSLATION") {
    return;
  }
  // eslint-disable-next-line no-console
  console.error("[react-intl]", error);
};

/**
 * Provides the root react-intl context for the application.
 *
 * @param {PropsWithChildren} props - Provider children to wrap with intl context.
 * @returns {React.JSX.Element} Intl provider tree.
 */
export const AppIntlProvider = (
  props: PropsWithChildren,
): React.JSX.Element => {
  const { children } = props;

  return (
    <IntlProvider
      locale={resolveLocale()}
      defaultLocale={DEFAULT_LOCALE}
      // Message descriptors currently provide their own defaultMessage values.
      messages={{}}
      onError={handleIntlError}
    >
      {children}
    </IntlProvider>
  );
};
